import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

import { Prettify } from '@/shared/lib/types';
import { VideoInfo } from '@/shared/types/Project';

export const UPLOAD_STATUSES = {
  done: 'done',
  error: 'error',
  pending: 'pending',
  ready: 'ready',
  uploading: 'uploading',
} as const;

type UploadStatus = (typeof UPLOAD_STATUSES)[keyof typeof UPLOAD_STATUSES];

export type LocalFile = {
  id: string;
  name: string;
  error?: null | string;
  file: File;
  status: UploadStatus;
};

export type FileByLink = Prettify<
  {
    url: string;
    videoInfo: VideoInfo;
  } & Omit<LocalFile, 'file'>
>;

type State = {
  fileByLink: FileByLink | null;
  fileByLinkProgress: number;
  files: LocalFile[];
  filesProgress: Record<string, number>;
};

type Actions = {
  startFilesUpload: () => void;
  addFileByIdError: (id: string, error: null | string) => void;
  addFileByLinkError: (error: string) => void;
  addFileByLinkInfo: (videoInfo: VideoInfo) => void;
  addFiles: (files: File[]) => void;
  addPendingFileByLink: (url: string) => void;
  deleteFile: (id: string) => void;
  resetFiles: () => void;
  resetFileUploadProgress: (id: string) => void;
  setFileByLinkProgress: (progress: number) => void;
  setLinkedSource: (params: { name: string; url: string }) => void;
  updateFileUploadProgress: (id: string, progress: number) => void;
};

const initialState: State = {
  fileByLink: null,
  fileByLinkProgress: 0,
  files: [],
  filesProgress: {},
};

export const useUploadFileStore = create<Actions & State>((set, get) => ({
  ...initialState,

    startFilesUpload: () =>
    set((state) => ({
      ...state,
      files: state.files.map((file) => ({
        ...file,
        error: null,
        status: UPLOAD_STATUSES.uploading,
      })),
      filesProgress: Object.fromEntries(state.files.map((file) => [file.id, 0])),
    })),

  addFileByIdError: (id, error) => {
    const currentFile = get().files.find((file) => file.id === id);

    if (!currentFile) {
      return;
    }

    set((state) => ({
      ...state,
      files: state.files.map((file) => (file.id === id ? { ...file, error, status: UPLOAD_STATUSES.error } : file)),
      filesProgress: {
        ...state.filesProgress,
        [id]: 0,
      },
    }));
  },

  addFileByLinkError: (error) => {
    const currentFileByLink = get().fileByLink;

    if (!currentFileByLink) {
      return;
    }

    set((state) => ({
      ...state,
      fileByLink: {
        ...currentFileByLink,
        error,
        status: UPLOAD_STATUSES.error,
      },
    }));
  },
  
  addFileByLinkInfo: (videoInfo) => {
    const currentFileByLink = get().fileByLink;

    if (!currentFileByLink) {
      return;
    }

    set((state) => ({
      ...state,
      fileByLink: {
        ...currentFileByLink,
        name: videoInfo.title,
        error: null,
        progress: 0,
        status: UPLOAD_STATUSES.ready,
        videoInfo,
      },
    }));
  },

  addFiles: (files) => {
    const newFiles = files.map((file) => ({
      id: uuidv4(),
      name: file.name,
      error: null,
      file,
      progress: 0,
      status: UPLOAD_STATUSES.ready,
    }));

    set((state) => ({
      files: [...state.files, ...newFiles],
    }));
  },

  addPendingFileByLink: (url) =>
    set((state) => ({
      ...state,
      fileByLink: {
        id: url,
        name: 'File by link',
        error: null,
        progress: 0,
        status: UPLOAD_STATUSES.pending,
        url,
        videoInfo: { title: 'File by link', duration_seconds: 0, preview: '' },
      },
    })),

  deleteFile: (id) =>
    set((state) => ({
      ...state,
      files: state.files.filter((file) => file.id !== id),
    })),

  resetFiles: () => set({ ...initialState }),

  resetFileUploadProgress: (id) => {
    const currentFile = get().files.find((file) => file.id === id);

    if (!currentFile) {
      return;
    }

    set((state) => ({
      ...state,
      files: state.files.map((file) => (file.id === id ? { ...file, status: UPLOAD_STATUSES.ready } : file)),
      filesProgress: {
        ...state.filesProgress,
        [id]: 0,
      },
    }));
  },

  setFileByLinkProgress: (progress: number) =>
    set((state) => ({
      ...state,
      fileByLinkProgress: progress,
    })),

  setLinkedSource: ({ name, url }) =>
    set((state) => ({
      ...state,
      fileByLink: {
        id: url,
        name,
        error: null,
        progress: 0,
        status: UPLOAD_STATUSES.ready,
        url,
        videoInfo: { title: name, duration_seconds: 0, preview: '' },
      },
    })),

  updateFileUploadProgress: (id, progress) => {
    const currentFile = get().files.find((file) => file.id === id);
    const currentProgress = get().filesProgress[id];
    const newStatus = progress === 100 ? UPLOAD_STATUSES.done : UPLOAD_STATUSES.uploading;
    const isStatusUpdated = currentFile?.status !== newStatus;

    if (!currentFile || currentProgress === progress) {
      return;
    }

    set((state) => ({
      ...state,
      ...(isStatusUpdated
        ? { files: state.files.map((file) => (file.id === id ? { ...file, progress, status: newStatus } : file)) }
        : undefined),
      filesProgress: {
        ...state.filesProgress,
        [id]: progress,
      },
    }));
  },
}));

export const hasFileUploadErrorSelector = (state: State) =>
  state.files.some((file) => file.status === UPLOAD_STATUSES.error);

export const isBulkUploadSelector = (state: State) => state.files.length > 1;
